import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal, { SweetAlertResult } from 'sweetalert2';

import IdentityProvider from 'classes/StandingData/IdentityProvider/Search/IdentityProvider';
import IdentityProviderList from 'classes/StandingData/IdentityProvider/Search/IdentityProviderList';
import Search from 'classes/StandingData/IdentityProvider/Search/Search';
import IdentityProviderDetailComponent from 'components/desktop/StandingData/IdentityProvider/Detail/IdentityProviderDetailComponent';
import BtnGroup, { BtnData } from 'components/desktop/_general/Button/BtnGroup/BtnGroup';
import BtnNew from 'components/desktop/_general/Button/BtnNew/BtnNew';
import { RenderIf } from 'components/desktop/_general/Conditional/RenderIf';
import LoadingAnimation from 'components/desktop/_general/Loading/LoadingAnimation';
import Pagination from 'components/desktop/_general/Pagination/Pagination';
import AdvancedSearchInput from 'components/desktop/_general/Search/AdvancedSearchInput';
import StandingDataList, {
	Row,
} from 'components/desktop/_general/StandingDataList/StandingDataList';
import clone from 'functions/clone';
import { QueryParams, getQueryParams } from 'functions/getQueryParams';
import useAirNavigation from 'hooks/useAirNavigation';
import useEffectWhenMounted from 'hooks/useEffectWhenMounted';
import { ISelectItem } from 'interfaces/ISelectItem';
import { RootState } from 'reducers/rootReducer';

interface IProps {
	archive?: boolean;
}

const maxNumberOfButtons = 5;

const IdentityProviderManagementComponent = (props: IProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const reduxCultureCode: string = useSelector((state: RootState) => state.cultureCode);

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const { params } = useAirNavigation();
	const displayOverlay = params.get('id') ? true : false;

	const [identityProviderList, setIdentityProviderList] = useState<IdentityProviderList>(
		new IdentityProviderList()
	);
	const [search, setSearch] = useState<Search>(new Search());
	const [searchTerm, setSearchTerm] = useState<string>(search.searchTerm.value ?? '');

	useEffect(() => {
		initialize();
	}, []);

	useEffectWhenMounted(() => {
		initialize();
	}, [reduxCultureCode]);

	const initialize = async (): Promise<void> => {
		if (props.archive !== undefined && props.archive == true) {
			search.showOnlyDeleted = true;
		}
		if (window.location.href.includes('?')) {
			const queryParams: QueryParams = getQueryParams();
			search.mapFromUrl(queryParams);
			await getIdentityProviders();
		} else {
			await getIdentityProviders();
		}
		setSearch(clone(search));
	};

	const getIdentityProviders = async () => {
		setIsLoading(true);
		const response = await identityProviderList.callApi(search.mapToRequest());
		response.do((x) => {
			identityProviderList.mapFromApi(x);
			setIdentityProviderList(clone(identityProviderList));
		});
		setIsLoading(false);
	};

	const getSuggestedIdentityProviders = async (): Promise<ISelectItem[]> => {
		if (searchTerm.length >= 1) {
			await identityProviderList.search(searchTerm, props.archive ?? false);
		} else {
			identityProviderList.filtered = [];
		}

		setIdentityProviderList(clone(identityProviderList));
		return identityProviderList.filtered;
	};

	const handleChangeUrl = () => {
		const url: string | null = search.mapToUrl();
		if (url) navigate(url);
		getIdentityProviders();
	};

	const handleSearch = (input?: string) => {
		search.setSearchTerm(input ?? searchTerm);
		handleChangeUrl();
	};

	const handleChangePageIndex = (index: number) => {
		search.pageIndex.value = index;
		handleChangeUrl();
	};

	const handleChangeId = (id: string | null) => {
		search.id.value = id;
		handleChangeUrl();
	};

	const handleOverlayOpen = (input: string): void => {
		handleChangeId(input);
	};

	const handleOverlayClose = (): void => {
		handleChangeId(null);
	};

	const handleArchiveIdentityProvider = async (
		identityProvider: IdentityProvider
	): Promise<void> => {
		const result: SweetAlertResult = await Swal.fire({
			title: t('standingData:ARCHIVE_TITLE'),
			text: t('standingData:IDENTITY_PROVIDER_ARCHIVE_DIALOG'),
			icon: 'warning',
			showCancelButton: true,
			iconColor: '#ff0004',
			confirmButtonColor: '#ff0004',
			confirmButtonText: t('_general:ARCHIVE'),
			cancelButtonText: t('_general:CANCEL'),
		});
		if (result.value) {
			setIsLoading(true);
			try {
				await identityProvider.callApiDelete();
				await Swal.fire({
					title: t('standingData:IDENTITY_PROVIDER_ARCHIVE_CONFIRMATION'),
					confirmButtonColor: '#00cc07',
				});
				await getIdentityProviders();
			} finally {
				setIsLoading(false);
			}
		}
	};

	const handleRestoreIdentityProvider = async (
		identityProvider: IdentityProvider
	): Promise<void> => {
		const result: SweetAlertResult = await Swal.fire({
			title: t('standingData:RESTORE_TITLE'),
			text: t('standingData:IDENTITY_PROVIDER_RESTORE_DIALOG'),
			icon: 'warning',
			showCancelButton: true,
			iconColor: '#00cc07',
			confirmButtonColor: '#00cc07',
			confirmButtonText: t('_general:RESTORE'),
			cancelButtonText: t('_general:CANCEL'),
		});
		if (result.value) {
			setIsLoading(true);
			try {
				await identityProvider.callApiRestore();
				await Swal.fire({
					title: t('standingData:IDENTITY_PROVIDER_RESTORE_CONFIRMATION'),
					confirmButtonColor: '#00cc07',
				});
				await getIdentityProviders();
			} finally {
				setIsLoading(false);
			}
		}
	};

	const handleChangeIdToNew = (): void => {
		handleChangeId('new');
	};

	const getButtonDataArchive = (input: IdentityProvider): BtnData | undefined => {
		if (props.archive) return;
		return {
			onClick: () => handleArchiveIdentityProvider(input),
		};
	};

	const getButtonDataEdit = (id: string): BtnData | undefined => {
		if (props.archive) return;
		return {
			onClick: () => handleOverlayOpen(id),
		};
	};

	const getButtonDataRestore = (input: IdentityProvider): BtnData | undefined => {
		if (!props.archive) return;
		return {
			onClick: () => handleRestoreIdentityProvider(input),
		};
	};

	const renderButtons = (input: IdentityProvider): JSX.Element => {
		return (
			<BtnGroup
				edit={getButtonDataEdit(input.id)}
				archive={getButtonDataArchive(input)}
				restore={getButtonDataRestore(input)}
			/>
		);
	};

	const renderListRows = (): Row[] => {
		return identityProviderList.all.map((identityProvider: IdentityProvider) => {
			return {
				id: identityProvider.id,
				jsx: (
					<div
						className="d-flex flex-row justify-content-start align-items-center"
						style={{ gap: '2em' }}
					>
						<RenderIf condition={identityProvider.iconSvgUrl != null}>
							<img
								src={identityProvider.iconSvgUrl ?? ''}
								style={{ width: '40px', height: '40px' }}
							></img>
						</RenderIf>
						<div className="flex-fill">{identityProvider.name}</div>
						<div>{renderButtons(identityProvider)}</div>
					</div>
				),
			};
		});
	};

	return (
		<main className="container">
			<div className="d-flex flex-row justify-content-between">
				{!props.archive ? (
					<>
						<h1>{t('standingData:IDENTITY_PROVIDER_MANAGEMENT')}</h1>
						<div className="d-flex flex-gap-25">
							<BtnNew
								onClick={handleChangeIdToNew}
								name={t('standingData:CREATE_IDENTITY_PROVIDER')}
							/>
						</div>
					</>
				) : (
					<h1>{t('standingData:IDENTITY_PROVIDER_ARCHIVE')}</h1>
				)}
			</div>
			<br />
			<div className="row">
				<div className="col-lg-12">
					<div className="input-group mb-3">
						<AdvancedSearchInput
							debounceMilliSeconds={350}
							getSuggestedItems={getSuggestedIdentityProviders}
							handleSearch={handleSearch}
							searchTerm={searchTerm}
							setSearchTerm={setSearchTerm}
							triggerSearchLetterAmount={1}
						/>
					</div>
				</div>
			</div>
			<br />
			<LoadingAnimation isLoading={isLoading} />
			<p>{identityProviderList.totalCount + ' ' + t('_general:IDENTITY_PROVIDERS')}</p>
			<StandingDataList
				archive={props.archive}
				displayOverlay={displayOverlay}
				rows={renderListRows()}
				overlay={<IdentityProviderDetailComponent />}
				handleOverlayOpen={handleOverlayOpen}
				handleOverlayClose={handleOverlayClose}
			/>
			<br />
			<Pagination
				countOfAllListItems={identityProviderList.totalCount}
				pageIndex={search.getPageIndex()}
				setPageIndex={handleChangePageIndex}
				listItemAmount={search.getPageSize()}
				maxNumberOfButtons={maxNumberOfButtons}
			/>
		</main>
	);
};

export default IdentityProviderManagementComponent;
